import { find, filter, first, some } from 'lodash';
import { IPatchOperation, PatchOpType } from "../modelFactory";
import siteVisitFactory, { ISiteVisit, SiteVisitType } from "./siteVisitModel";
import { Iterable } from "immutable";
import jobCostFactory, { IJobCost } from "./jobCostModel";
import { ISiteVisitJobCost } from './siteVisitJobCostModel';
import jobFactory, { IJob } from './jobModel';
import { IChangeOrder } from './changeOrderModel';
import { IInstallSheet } from './installSheetModel';
import jobIssueFactory, { IJobIssue } from './jobIssueModel';
import { ChecklistItemStatus, IInspectionChecklistItem } from './inspectionChecklistItemModel';

// export interface IInspectionIssue { 
// 	visitKey: string;
// 	type: JobIssueT
// 	disabled: boolean; 
// 	contract: IJobCost;
// 	changeOrder?: IChangeOrder;
// 	installSheet?: IInstallSheet;
// }

type PartialWithRequired<T, K extends keyof T> = Partial<T> & Pick<Required<T>, K>;
interface ITimeLogItem {
	startMs: number;
	endMs?: number;
}

function incrementVersion(currentVersion = 0) {
	if(currentVersion === Number.MAX_SAFE_INTEGER) {
		return 0;
	}

	return currentVersion + 1;
}

class InspectionModelFacade {
	private _issues: IJobIssue[];
	private _timeLog: ITimeLogItem[];
	private _visit: ISiteVisit;
	private _version: number;

	constructor(visit: ISiteVisit, issues: IJobIssue[] = [], timeLog: ITimeLogItem[] = [], version = 0) {
		this._issues = issues;
		this._timeLog = timeLog;
		this._visit = visit;
		this._version = version;
	}

	addIssue(issue: Partial<IJobIssue>) {
		this._issues.push(jobIssueFactory(issue));
	}

	updateIssue(issue: IJobIssue) {
		const idx = this.issues.findIndex(i => i.key === issue.key);

		if(idx !== -1) {
			this._issues.splice(idx, 1, issue);
		}
	}

	removeIssue(key: string) {
		const idx = this.issues.findIndex(i => i.key === key);

		if(idx !== -1) {
			this._issues.splice(idx, 1);
		}
	}

	logTime(logItem: ITimeLogItem) {
		const existing = this.timeLog.find(i => i.startMs === logItem.startMs);

		if(existing) {
			existing.endMs = logItem.endMs;
		}
		else {
			this.timeLog.push(logItem);
		}
	}

	setChecklistItem(item: IInspectionChecklistItem) {
		this._visit = siteVisitFactory(this._visit.toDocument());

		const existing = item.key && this._visit.inspectionChecklistItems[item.key];

		if(existing) {
			if(item.status === ChecklistItemStatus.unknown) {
				this._visit.inspectionChecklistItems.delete(existing.key);
			}
			else {
				const patch = item.getPatch(existing);

				existing.applyPatch(patch);
			}
		}
		else if(item.status !== ChecklistItemStatus.unknown) {
			this._visit.inspectionChecklistItems.add(item);
		}
	}

	incrementVersion() {
		this._version = incrementVersion(this._version);
	}

	get issues() {
		return this._issues;
	}

	get visit() {
		return this._visit;
	}

	get timeLog() {
		return this._timeLog;
	}

	get version() {
		return this._version;
	}

	get canFinalize() {
		return this._visit.isValid() && !!this.visit.installationQuality;
	}

	// TODO: evaluate if needsRebase makes sense in a theoretical situation where a visit is altered while an inspection is in progress
	// needsRebase(storeVisit: ISiteVisit, storeJobCosts: Iterable<string, IJobCost>, storeVisits: Iterable<string, ISiteVisit>) {
	// 	if(this._job.key !== storeJob.key || this._job.itemVersion !== storeJob.itemVersion) {
	// 		return true;
	// 	}
		
	// 	if(storeJobCosts.some(jc => !this._jobCosts.find(jc2 => jc2.key === jc.key && jc2.itemVersion === jc.itemVersion))) {
	// 		return true;
	// 	}
	// 	// status log check is necessary because the dashboard list shape does not include the status log
	// 	return storeVisits.some(v => !this._visits.find(sv => sv.key === v.key && sv.itemVersion === v.itemVersion && sv.jobCosts.length() === v.jobCosts.length() && sv.statusLog?.length() === v.statusLog?.length()));
	// }

	getPatch(target: InspectionModelFacade) {
		const patch:IPatchOperation[] = [];
		const visitPatch = this.visit.getPatch(target.visit);

		if(visitPatch.length) {
			patch.push({ op: PatchOpType.replace, path: `visit/${this.visit.key}`, value: visitPatch });
		}

		for(const issue of this.issues) {
			const targetIssue = target.issues.find(i => i.key === issue.key);

			if(targetIssue) {
				const targetPatch = issue.getPatch(targetIssue);
				if(targetPatch.length) {
					patch.push({ op: PatchOpType.replace, path: `issue/${issue.key}`, value: targetPatch});
				}
			}
			else {
				patch.push({ op: PatchOpType.add, path: 'issue', value: issue.toDocument() })
			}
		}

		for(const targetIssue of target.issues) {
			const issue = this._issues.find(i => i.key === targetIssue.key);

			if(!issue) {
				patch.push({ op: PatchOpType.remove, path: `issue/${targetIssue.key}`});
			}
		}

		for(const logItem of this.timeLog) {
			const existing = target.timeLog.find(l => l.startMs === logItem.startMs);

			if(existing) {
				if(existing.endMs !== logItem.endMs) {
					patch.push({ op: PatchOpType.replace, path: `timeLog/${logItem.startMs}}`, value: logItem});
				}
			}
			else {
				patch.push({ op: PatchOpType.add, path: 'timeLog', value: logItem })
			}
		}

		return patch;
	}
	toDocument () {
		const result: Record<string, any> = {visit: this._visit?.toDocument(), issues: [], timeLog: this.timeLog.concat(), version: this.version }

		for(const issue of this._issues) {
			result.issues.push(issue.toDocument());
		}

		return result;
	}
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export type IInspectionModelFacade = Omit<InspectionModelFacade, "constructor">
export function inspectionModelFacadeFactory(doc?: Partial<IInspectionModelFacade>): IInspectionModelFacade {
	const visit = doc?.visit? siteVisitFactory(doc.visit): null;
	let issues = [];
	
	if(doc?.issues) {
		issues = doc.issues.map(jobIssueFactory)
	}

	return new InspectionModelFacade(visit, issues, doc?.timeLog, doc?.version);
}